import { Observer } from "mobx-react";
import React, { Component } from "react";
import { throttle } from "lodash";
import { Spinner } from "@components";
import { VirtualSelectorStore } from "./VirtualSelectorStore";
import { SearchItem } from "./utils";
import { Typography } from "@material-ui/core";

type Props<SEARCH_ITEM extends SearchItem> = {
  renderItem: (data: SEARCH_ITEM, selected: boolean) => React.ReactNode;
  store: VirtualSelectorStore<any, SEARCH_ITEM, any>;
  waitingContent?: React.ReactNode;
};

type State = {
  isLoading: boolean;
  currentPage: number;
};

class VirtualSelectorSearchResult<SEARCH_ITEM extends SearchItem> extends Component<
  Props<SEARCH_ITEM>,
  State
> {
  private scrollContainerRef: React.RefObject<HTMLDivElement>;

  constructor(props: Props<SEARCH_ITEM>) {
    super(props);
    this.state = {
      isLoading: false,
      currentPage: 1
    };
    this.scrollContainerRef = React.createRef();
  }

  componentDidMount() {
    this.loadNextPage();
    window.addEventListener("scroll", this.handleScroll);
  }

  componentWillUnmount() {
    window.removeEventListener("scroll", this.handleScroll);
  }

  handleScroll = throttle(
    () => {
      const { isLoading } = this.state;
      if (isLoading) return;

      const scrollContainer = this.scrollContainerRef.current;

      if (scrollContainer) {
        const scrollHeight = scrollContainer.scrollHeight;
        const scrollTop = scrollContainer.scrollTop;

        if (scrollHeight - scrollTop <= 3000) {
          this.loadNextPage();
        }
      }
    },
    200,
    { leading: true }
  );

  loadNextPage = async () => {
    const { store } = this.props;
    const { currentPage, isLoading } = this.state;

    if (isLoading) return;

    this.setState({ isLoading: true });

    try {
      const limit = 20; // You can adjust the limit as needed
      const skip = (currentPage - 1) * limit;
      await store.fetchMore(skip, limit);
      this.setState((prevState) => ({
        isLoading: false,
        currentPage: prevState.currentPage + 1
      }));
    } catch (error) {
      console.error("Error loading more data", error);
      this.setState({ isLoading: false });
    }
  };

  render() {
    const { store } = this.props;
    const { isLoading } = this.state;

    return (
      <Observer>
        {() => (
          <div
            ref={this.scrollContainerRef}
            style={{ overflowY: "scroll", height: "100%", width: "100%" }}
            onScroll={this.handleScroll}
          >
            <div style={{ padding: "8px" }}>
              {store.searchResult.map((data) => (
                <div key={data._id} style={{ padding: "4px 8px" }}>
                  {this.props.renderItem(
                    data,
                    store.selected.some((s) => s._id === data._id)
                  )}
                </div>
              ))}

              {isLoading && (
                <div
                  style={{ display: "flex", justifyContent: "center", padding: "20px" }}
                >
                  <Spinner />
                </div>
              )}

              {store.isWaiting && !store.searchResult.length && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: "20px"
                  }}
                >
                  <Typography variant="body1" style={{ fontWeight: 500 }}>
                    {this.props.waitingContent || "Waiting for input"}
                  </Typography>
                </div>
              )}
            </div>
          </div>
        )}
      </Observer>
    );
  }
}

export default VirtualSelectorSearchResult;
